import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  Typography
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";
import {
  changeSopApplicationStatusAction,
  leadDocumentDeleteAction,
  leadCriminalAndSecurityFindingAction,
  leadCriminalAndSecurityUploadingAction
} from "../../../actions/leadActions/leadDocsActions";
import { findcountryMasterAddAction } from "../../../actions/countryMasterAction";
import { successTrueMsgAction } from "../../../actions/globalAction";
import CountryFlag from "../../customComponent/CountryFlag";
import { IoEyeOutline } from "react-icons/io5";
import { CiRepeat } from "react-icons/ci";
import { GoDownload } from "react-icons/go";
import { AiTwotoneDelete } from "react-icons/ai";

const CriminalAndSecurity = ({ handleCompleteTick }) => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const [hoveredIcon, setHoveredIcon] = useState(null);
  const [statusVerify, setStatusVerify] = useState({});

  // Selectors
  const {
    countryFindSuccess,
    leadCriminalFindingSuccess,
    leadCriminalUploadingSuccess,
    leadDocumentDeleteSuccess,
    successAlertMsgSuccess
  } = useSelector((state) => ({
    countryFindSuccess: state.findcountryMasterAdd.countryFindSuccess,
    leadCriminalFindingSuccess: state.leadCriminalFinding.leadCriminalFindingSuccess,
    leadCriminalUploadingSuccess: state.leadCriminalUploading.leadCriminalUploadingSuccess,
    leadDocumentDeleteSuccess: state.leadDocumentDelete.leadDocumentDeleteSuccess,
    successAlertMsgSuccess: state.successTrueMsg.successAlertMsgSuccess
  }));

  const { leadSopStatusChangeSuccess } = useSelector((state) => state.changeSopApplicationStatus);

  // Memoized country list calculation
  const countryList = useMemo(() => {
    if (countryFindSuccess && leadCriminalFindingSuccess) {
      return countryFindSuccess.filter(country => 
        !leadCriminalFindingSuccess.some(t => t.countryName === country.countryName)
      );
    }
    return [];
  }, [countryFindSuccess, leadCriminalFindingSuccess]);

  // Fetch initial data
  useEffect(() => {
    dispatch(leadCriminalAndSecurityFindingAction(id));
    dispatch(findcountryMasterAddAction(id, "criminal"));
  }, [dispatch, id, leadCriminalUploadingSuccess, leadDocumentDeleteSuccess, leadSopStatusChangeSuccess, successAlertMsgSuccess]);

  // Success alerts
  useEffect(() => {
    if (leadCriminalUploadingSuccess && successAlertMsgSuccess) {
      Swal.fire({
        icon: "success",
        title: "Good job \u{1F44D}",
        text: "Criminal & Security Uploaded Successfully"
      });
      dispatch(successTrueMsgAction(false));
      dispatch({ type: "LEAD_CRIMINAL_UPLOADING_RESET" });
    }
  }, [leadCriminalUploadingSuccess, successAlertMsgSuccess, dispatch]);

  // Handlers
  const handleDocChange = useCallback((event, countryId, countryName) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      dispatch(leadCriminalAndSecurityUploadingAction(
        reader.result, 
        countryId, 
        id, 
        countryName
      ));
      dispatch(successTrueMsgAction(true));
    };
    reader.readAsDataURL(file);
  }, [dispatch, id]);

  const toggleStatusVerify = useCallback((docId, value) => {
    setStatusVerify(prev => ({ ...prev, [docId]: value }));
  }, []);

  const handleDeleteDocument = useCallback((docId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(leadDocumentDeleteAction(docId, id, "criminal"));
        Swal.fire("Deleted!", "Your file has been deleted.\u{1F44D}", "success");
      }
    });
  }, [dispatch, id]);

  const handleStatusChange = useCallback((leadId, countryId, status) => {
    dispatch(changeSopApplicationStatusAction(
      leadId, 
      countryId, 
      status, 
      "criminal"
    ));
  }, [dispatch]);

  // Style configuration
  const iconStyle = {
    width: "22px",
    height: "22px",
    marginRight: "10px",
    transition: "color 0.3s ease"
  };

  const handleMouseEnter = useCallback((icon) => setHoveredIcon(icon), []);
  const handleMouseLeave = useCallback(() => setHoveredIcon(null), []);

  // Render helpers
  const renderUploadBox = (data) => (
    <Box
      sx={{
        height: 82,
        bgcolor: "white",
        border: "1.5px dashed rgba(25, 118, 210, 0.5)",
        borderRadius: 1
      }}
    >
      <label
        htmlFor={`criminal-file-${data.id}`}
        style={{ 
          margin: 15,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer"
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <CountryFlag
            countryCode={data.countryCode}
            component="stepper"
            width={39}
            height={39}
          />
          <Typography variant="body1" ml={1.25}>
            {data.countryName.split("(")[0]}
          </Typography>
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <img src="/menuIcons/uploadBlue.png" alt="upload" />
          <Typography color="primary" ml={1} fontFamily="Montserrat">
            Upload
          </Typography>
        </div>
      </label>
      <input
        type="file"
        accept=".pdf,.doc,.docx,.xlsx"
        id={`criminal-file-${data.id}`}
        hidden
        onChange={(e) => handleDocChange(e, data.id, data.countryName)}
      />
    </Box>
  );

  const renderStatusChips = (criminalData) => (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backdropFilter: "blur(8px)",
        gap: 2,
        opacity: 0,
        transition: "opacity 0.3s",
        zIndex: 1,
        "&:hover": { opacity: 1 }
      }}
    >
      <Chip
        label="Reupload"
        color="warning"
        variant="outlined"
        sx={{ 
          borderColor: "warning.main",
          color: "warning.main",
          cursor: "pointer"
        }}
        onClick={() => {
          handleStatusChange(criminalData.leadId, criminalData.countryId, "reupload");
          toggleStatusVerify(criminalData.id, false);
        }}
      />
      <Chip
        label="Verify"
        color="success"
        variant="outlined"
        sx={{
          borderColor: "success.main",
          color: "success.main",
          cursor: "pointer"
        }}
        onClick={() => {
          handleStatusChange(criminalData.leadId, criminalData.countryId, "verified");
          toggleStatusVerify(criminalData.id, false);
        }}
      />
      <Chip
        label="Close"
        color="error"
        variant="outlined"
        sx={{ cursor: "pointer" }}
        onClick={() => toggleStatusVerify(criminalData.id, false)}
      />
    </Box>
  );

  const renderActionIcons = (criminalData) => (
    <>
      <a
        href={criminalData.pdfUrl}
        download
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: "none", color: "black" }}
      >
        <Tooltip title="View">
          <IconButton>
            <IoEyeOutline style={{
              ...iconStyle,
              color: hoveredIcon === "view" ? "red" : "black",
              cursor: "pointer"
            }}
              onMouseEnter={() => handleMouseEnter("view")}
              onMouseLeave={handleMouseLeave}
            />
          </IconButton>
        </Tooltip>
      </a>

      {criminalData.status !== "approved" && (
        <Tooltip title="Change status">
          <IconButton
            sx={{ padding: 0, marginRight: "10px" }}
            onClick={() => toggleStatusVerify(criminalData.id, true)}
          >
            <CiRepeat style={{
              ...iconStyle,
              color: hoveredIcon === "repeat" ? "red" : "black"
            }}
            onMouseEnter={() => handleMouseEnter("repeat")}
            onMouseLeave={handleMouseLeave}
             />
          </IconButton>
        </Tooltip>
      )}

      <a
        href={criminalData.pdfUrl}
        download
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: "none", color: "black" }}
      >
        <Tooltip title="Download">
          <IconButton>
            <GoDownload style={{
              ...iconStyle,
              color: hoveredIcon === "download" ? "red" : "black"
            }}
              onMouseEnter={() => handleMouseEnter("download")}
              onMouseLeave={handleMouseLeave}
            />
          </IconButton>
        </Tooltip>
      </a>

      {!["verified", "approved"].includes(criminalData.status) && (
        <Tooltip title="Delete">
          <IconButton onClick={() => handleDeleteDocument(criminalData.id)}>
            <AiTwotoneDelete style={{
              ...iconStyle,
              color: hoveredIcon === "delete" ? "red" : "black"
            }}
              onMouseEnter={() => handleMouseEnter("delete")}
              onMouseLeave={handleMouseLeave}
            />
          </IconButton>
        </Tooltip>
      )}
    </>
  );

  return (
    <Grid container spacing={2} p={1.25}>
      {countryList.length === 0 && leadCriminalFindingSuccess?.length === 0 && (
        <Typography
          variant="body1"
          textAlign="center"
          mt={10}
          fontFamily="Montserrat"
        >
          No documents to show. Add your preferred country or Add document in Knowledge bank
        </Typography>
      )}

      {countryList.map((data) => (
        <Grid item xs={6} md={4} lg={3} key={data.id}>
          <Box my={2}>{renderUploadBox(data)}</Box>
        </Grid>
      ))}

      {leadCriminalFindingSuccess?.map((criminalData) => (
        <Grid item xs={6} md={4} lg={3} key={criminalData.id}>
          <Box mt={1} mb={5} position="relative">
            <Box
              sx={{
                height: 82,
                bgcolor: "white",
                border: "1.5px dashed rgba(255, 153, 0, 1)",
                borderRadius: 2
              }}
            >
              <Box display="flex" justifyContent="space-between" m={1.25}>
                <Box display="flex" alignItems="center">
                  <CountryFlag
                    countryCode={criminalData.countryCode.countryCode}
                    component="stepper"
                    width={39}
                    height={39}
                  />
                  <Box ml={1.25}>
                    <Typography variant="body1">
                      {criminalData.countryName.split("(")[0]}
                    </Typography>
                    <Box display="flex">
                      {!statusVerify[criminalData.id] && renderActionIcons(criminalData)}
                    </Box>
                  </Box>
                </Box>

                <Box display="flex" flexDirection="column" alignItems="flex-end">
                  <Typography color="success.main" fontFamily="Montserrat">
                    Uploaded
                  </Typography>
                  <Typography
                    color={["verified", "approved"].includes(criminalData.status) 
                      ? "success.main" 
                      : "warning.main"}
                    textTransform="capitalize"
                  >
                    {criminalData.status}
                  </Typography>
                </Box>
              </Box>
              {statusVerify[criminalData.id] && renderStatusChips(criminalData)}
            </Box>
          </Box>
        </Grid>
      ))}
    </Grid>
  );
};

export default React.memo(CriminalAndSecurity);